import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { marketAPI, paperAPI } from '../../services/api'
import { PAPER_POSITIONS_PATH } from '../../routes'
import type { PaperPosition, SparklineSeries } from '../../types'
import { formatPrice } from '../../utils/marketPrice'
import { formatPnlPct } from '../../utils/pnl'
import { PaperOpenCounts, loadPaperOpenCounts, plural } from '../../utils/paperCounts'
import GradientSparkline from './GradientSparkline'
import TerminalPanel from './TerminalPanel'
import { directionOf, useChartTheme } from './chartTheme'
import { displaySymbol } from '../../utils/displaySymbol'

export const MAX_PAPER_ROWS = 6
export const PAPER_POSITIONS_POLL_MS = 60_000

const PNL_TEXT = { up: 'text-green-400', down: 'text-red-400', flat: 'text-gray-400' } as const

const ROW_GRID = 'grid grid-cols-[minmax(0,1fr)_4.5rem_4.75rem] gap-3 sm:grid-cols-[minmax(0,1fr)_7.5rem_5.5rem_5.5rem]'

/**
 * Every open sub-position in one coin. The strategy holds a coin once per
 * breakout window that fired (up to four); the ledger keeps them separate and
 * so does the API. This only groups them for display.
 */
export interface PaperCoinGroup {
  symbol: string
  positions: PaperPosition[]
  /** Breakout windows holding the coin, shortest first. */
  windows: number[]
  /** Units-weighted average fill: the cost basis of the combined holding. */
  avgEntry: number
  minEntry: number
  maxEntry: number
  minStop: number
  maxStop: number
}

/** One group per coin, in the order each coin first appears. */
export const groupPaperPositions = (positions: PaperPosition[]): PaperCoinGroup[] => {
  const bySymbol = new Map<string, PaperPosition[]>()
  for (const position of positions) {
    const rows = bySymbol.get(position.symbol)
    if (rows) rows.push(position)
    else bySymbol.set(position.symbol, [position])
  }
  return Array.from(bySymbol, ([symbol, rows]) => {
    const entries = rows.map((row) => row.entry_price)
    const stops = rows.map((row) => row.stop_price)
    const units = rows.reduce((sum, row) => sum + row.units, 0)
    const cost = rows.reduce((sum, row) => sum + row.units * row.entry_price, 0)
    return {
      symbol,
      positions: rows,
      windows: rows.map((row) => row.window).sort((a, b) => a - b),
      // Falls back to a plain mean if units are missing, so a bad row can't divide by zero.
      avgEntry: units > 0 ? cost / units : entries.reduce((a, b) => a + b, 0) / entries.length,
      minEntry: Math.min(...entries),
      maxEntry: Math.max(...entries),
      minStop: Math.min(...stops),
      maxStop: Math.max(...stops),
    }
  })
}

/**
 * Unrealised move of the combined holding from its average fill, before fees.
 * Paper positions are long only (spec 2.1).
 */
const pctSinceEntry = (group: PaperCoinGroup, price: number) => ((price - group.avgEntry) / group.avgEntry) * 100

/** "$1.20", or "$1.10–$1.20" when the sub-positions differ. */
const priceRange = (min: number, max: number) =>
  min === max ? `$${formatPrice(min)}` : `$${formatPrice(min)}–$${formatPrice(max)}`

/**
 * The paper strategy's live positions, in the real Open positions panel's
 * shape: one row per coin, with a tag for each breakout window holding it.
 */
const PaperPositionsPanel = () => {
  const [positions, setPositions] = useState<PaperPosition[] | null>(null)
  // The open and coin counts: the list below is a capped page of rows.
  const [counts, setCounts] = useState<PaperOpenCounts | null>(null)
  const [failed, setFailed] = useState(false)
  const [sparklines, setSparklines] = useState<Record<string, SparklineSeries>>({})

  useEffect(() => {
    let active = true
    let loaded = false

    const load = async () => {
      if (loaded && document.hidden) return
      try {
        const [rows, nextCounts] = await Promise.all([
          paperAPI.positions({ status: 'open', limit: 100 }),
          loadPaperOpenCounts(),
        ])
        if (!active) return
        loaded = true
        setPositions(rows)
        setCounts(nextCounts)
        setFailed(false)

        const symbols = groupPaperPositions(rows)
          .slice(0, MAX_PAPER_ROWS)
          .map((group) => group.symbol)
        if (symbols.length === 0) return
        try {
          const data = await marketAPI.getSparklines(symbols)
          if (active) setSparklines(Object.fromEntries(data.sparklines.map((series) => [series.symbol, series])))
        } catch {
          // Display only: rows render without a price line.
        }
      } catch (error) {
        if (!active) return
        console.error('Paper positions refresh failed:', error)
        setFailed(true)
      }
    }

    load()
    const timer = setInterval(load, PAPER_POSITIONS_POLL_MS)
    return () => {
      active = false
      clearInterval(timer)
    }
  }, [])

  const groups = positions ? groupPaperPositions(positions) : []
  const coins = counts?.coins ?? null
  const shown = groups.slice(0, MAX_PAPER_ROWS)

  return (
    <TerminalPanel
      title="Paper open positions"
      tone="violet"
      className="h-full"
      subtitle={
        positions && (
          <p className="font-mono text-xs tabular-nums text-gray-500" data-testid="paper-positions-counts">
            {counts ? `${counts.open} open` : '— open'}
            {coins != null && ` · ${plural(coins, 'coin')}`} · pretend money
          </p>
        )
      }
    >
      {positions == null ? (
        <p className="py-10 text-center text-sm text-gray-500">
          {failed ? 'Paper positions are unavailable right now.' : 'Loading positions…'}
        </p>
      ) : shown.length === 0 ? (
        <div className="rounded-md border border-dashed border-terminal-border py-10 text-center">
          <p className="text-sm text-gray-300">No open paper positions</p>
          <p className="mt-1 text-xs text-gray-500">
            The strategy waits for a breakout to a new N-day high while BTC holds its 200-day average.
          </p>
        </div>
      ) : (
        <>
          <div
            className={`${ROW_GRID} hidden border-b border-terminal-border/70 px-3 pb-2 text-[10px] font-medium uppercase tracking-wider text-gray-500 sm:grid`}
            aria-hidden="true"
          >
            <span>Position</span>
            <span>7d price</span>
            <span className="text-right">P&amp;L</span>
            <span className="text-right">Stop</span>
          </div>
          <ul className="divide-y divide-terminal-border/40">
            {shown.map((group) => (
              <PaperRow key={group.symbol} group={group} sparkline={sparklines[group.symbol]} />
            ))}
          </ul>
          {/* The panel is a summary: it caps at MAX_PAPER_ROWS coins and sends
              the reader to the full ledger rather than growing inline. This used
              to be dead "+N more coins" text with nothing behind it. */}
          <div className="mt-3 text-center">
            <Link
              to={PAPER_POSITIONS_PATH}
              className="inline-flex items-center gap-1 rounded text-xs font-medium text-violet-300 transition-colors hover:text-violet-200 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
              data-testid="paper-positions-view-all"
            >
              {counts ? `View all ${plural(counts.open, 'position')}` : 'View all positions'}
              <span aria-hidden="true">&rarr;</span>
            </Link>
            {coins != null && coins > MAX_PAPER_ROWS && (
              <p className="mt-1 text-[11px] text-gray-500">
                {coins - MAX_PAPER_ROWS} more coin{coins - MAX_PAPER_ROWS === 1 ? '' : 's'} not shown here
              </p>
            )}
          </div>
        </>
      )}
    </TerminalPanel>
  )
}

const PaperRow = ({ group, sparkline }: { group: PaperCoinGroup; sparkline?: SparklineSeries }) => {
  const chart = useChartTheme()
  const [base, quote] = displaySymbol(group.symbol).split('/')
  const price = sparkline?.last_price
  const pnl = price != null && group.avgEntry > 0 ? pctSinceEntry(group, price) : null
  const direction = directionOf(pnl)
  const values = sparkline?.points.map((point) => point.close) ?? []
  const count = group.positions.length
  const mixedEntries = group.minEntry !== group.maxEntry
  const stops = priceRange(group.minStop, group.maxStop)
  const stopHint =
    group.minStop === group.maxStop
      ? '3-ATR trailing stop, ratchets up only'
      : 'Each window trails its own 3-ATR stop, ratcheting up only; the range spans them'

  return (
    <li
      className={`${ROW_GRID} items-center rounded-md px-2 py-3 transition-colors hover:bg-terminal-raised/60 sm:px-3`}
      data-testid="paper-position"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="truncate font-semibold text-gray-50">
            {base}
            {quote && <span className="hidden font-normal text-gray-500 sm:inline">/{quote}</span>}
          </span>
          <ul
            className="flex flex-wrap gap-1"
            aria-label={`Held by ${count} breakout window${count === 1 ? '' : 's'}: ${group.windows.join(', ')}-day`}
            data-testid="paper-position-windows"
          >
            {group.windows.map((window, index) => (
              <li
                key={`${window}-${index}`}
                className="inline-flex items-center rounded bg-violet-500/10 px-1 py-px font-mono text-[10px] font-bold uppercase leading-4 text-violet-300 ring-1 ring-inset ring-violet-500/30"
              >
                {window}D
              </li>
            ))}
          </ul>
        </div>
        <p className="mt-0.5 truncate font-mono text-[11px] tabular-nums text-gray-500" data-testid="paper-position-entry">
          {mixedEntries ? (
            <span title={`Units-weighted average $${formatPrice(group.avgEntry)}`}>
              entry {priceRange(group.minEntry, group.maxEntry)}
            </span>
          ) : (
            <>entry ${formatPrice(group.avgEntry)}</>
          )}
        </p>
        {/* The stop column is hidden on phones, so it gets its own line there. */}
        <p className="truncate font-mono text-[11px] tabular-nums text-gray-500 sm:hidden">stop {stops}</p>
      </div>

      <div className="h-10">
        {values.length > 1 ? (
          <GradientSparkline
            values={values}
            color={chart.colors[direction]}
            baseline={group.avgEntry}
            label={`${displaySymbol(group.symbol)} over 7 days, ${mixedEntries ? 'average entry' : 'entry'} level dashed`}
          />
        ) : (
          <div className="flex h-full items-center" aria-hidden="true">
            <span className="h-px w-full bg-terminal-border" />
          </div>
        )}
      </div>

      <p
        className={`text-right font-mono text-sm font-semibold tabular-nums ${PNL_TEXT[direction]}`}
        data-testid="paper-position-pnl"
        data-direction={direction}
        title={
          price != null
            ? `Last $${formatPrice(price)}, from ${mixedEntries ? 'the units-weighted average entry' : 'entry'} across ${count} sub-position${count === 1 ? '' : 's'}`
            : 'Latest price unavailable'
        }
      >
        {pnl == null ? '—' : formatPnlPct(pnl)}
      </p>

      <p
        className="hidden text-right font-mono text-xs tabular-nums text-gray-300 sm:block"
        aria-label={`Stop ${stops}`}
        title={stopHint}
        data-testid="paper-position-stop"
      >
        {group.minStop === group.maxStop ? (
          stops
        ) : (
          // Stacked so a range fits the column: low stop, then high.
          <>
            <span className="block">${formatPrice(group.minStop)}–</span>
            <span className="block">${formatPrice(group.maxStop)}</span>
          </>
        )}
      </p>
    </li>
  )
}

export default PaperPositionsPanel
